import { useState } from 'react';

import { Notice } from '@/components/Modal';
import {
  useMyAttendanceMonth,
  useMyAttendanceNow,
  useMyCorrections,
  useMyVoluntaryWorks,
  usePunch,
  useRequestCorrection,
  useRequestVoluntaryWork,
  useWithdrawCorrection,
  useWithdrawVoluntaryWork,
  type AttendanceRow,
} from '@/pages/mypage/useMyAttendance';
import { hourRatio, hourTone, useMyAttendanceHours } from '@/pages/hr/useWorkHours';

/**
 * 개인 출퇴근 (사용자 지시 2026-09-22 · 연차관리 안).
 *
 * <h2>찍는 것은 앱이 먼저다</h2>
 * 「실제 출결은 APP에서 할거고」 — 그래서 이 화면의 본업은 <b>보는 것</b>이다. 그래도 웹 버튼을
 * 둔 것은 사용자 결정이다(2026-09-22): 앱을 못 여는 날에도 출근이 사라지지 않아야 한다.
 *
 * <p><b>다음에 누를 것은 서버가 정한다</b>(`nextKind`). 화면이 「마지막이 출근이었으니 다음은
 * 퇴근」 을 스스로 셈하면 자정을 넘긴 날·어제 퇴근을 안 찍은 날에 판단이 갈린다.
 *
 * <h2>빠진 줄은 «정정 요청»으로</h2>
 * 지난 날을 여기서 메우지 않는다 — 메우게 두면 이 표가 「있었던 일」이 아니라 「적어 둔 일」이
 * 된다. 대신 인사총무팀에 <b>남는 요청</b>을 낸다(V326). 승인되면 그 자리에서 기록이 맞춰진다.
 */
export function AttendancePanel() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const now = useMyAttendanceNow();
  const view = useMyAttendanceMonth(month);
  const punch = usePunch();
  // 52시간 셈(V543) — 본사는 이번 주, 현장은 이번 달. 서버가 재시작 전이거나 인사자료가 없으면 줄을 안 그린다.
  const hours = useMyAttendanceHours().data;
  const hoursTone = hours ? hourTone(hours) : null;

  /** 정정 요청 폼을 연 날짜. `null` 이면 닫혀 있다. */
  const [correcting, setCorrecting] = useState<string | null>(null);
  /** 자발적 근무 신청 폼(V556). */
  const [volunteering, setVolunteering] = useState(false);

  const byDay = groupByDay(view.data?.rows ?? []);
  const nextKind = now.data?.nextKind ?? null;

  return (
    <div className="space-y-4">
      {/* ── 오늘 ── */}
      <div className="rounded-lg flex items-center gap-3 flex-wrap" style={{ border: '1px solid var(--border)', padding: '10px 12px' }}>
        <span style={{ fontSize: '13px', fontWeight: 700 }}>오늘</span>
        <span style={mutedText}>
          {now.data?.today ? `${KIND_LABEL[now.data.today.kind]} ${timeOf(now.data.today.occurredAt)}` : '아직 찍지 않았습니다.'}
        </span>
        {nextKind ? (
          <button
            className="btn-primary ml-auto"
            style={compactButton}
            disabled={punch.isPending}
            onClick={() => punch.mutate(nextKind)}
          >
            {punch.isPending ? '찍는 중...' : `${KIND_LABEL[nextKind]} 찍기`}
          </button>
        ) : (
          <span className="badge badge-green ml-auto">오늘 퇴근까지 완료</span>
        )}
      </div>
      {hours && (
        <div className="flex items-center gap-2" style={{ fontSize: '12.5px' }}>
          <span style={mutedText}>{hours.mode === 'MONTH_AVG' ? '이번 달 근무시간' : '이번 주 근무시간'}</span>
          <b
            className="num"
            style={hoursTone ? { color: hoursTone === 'red' ? 'var(--danger)' : '#f79009' } : undefined}
          >
            {hourRatio(hours)}
          </b>
          {hours.alert && !hours.locked && <span className="badge badge-yellow">근무시간 알림</span>}
          {hours.locked && <span className="badge badge-red">근무시간 알림</span>}
        </div>
      )}
      {/*
        자발적 근무 신청(V556 · 사용자 결정 2026-10-26) — 인사총무팀이 승인하면 그 시간대에 PC 가 잠기지 않고
        52시간 셈에서 빠진다. 잠긴 PC 에서는 이 화면을 못 여니 앱 출퇴근 화면에도 같은 칸이 있다.
      */}
      <div className="flex items-center gap-2">
        <button className="btn-secondary" style={compactButton} onClick={() => setVolunteering((prev) => !prev)}>
          자발적 근무 신청
        </button>
      </div>
      {volunteering && <VoluntaryForm onDone={() => setVolunteering(false)} />}
      <VoluntaryHistory />
      {punch.isError && <Notice tone="error">{punch.error.message}</Notice>}

      {/* ── 달 고르기 ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          className="input-field"
          type="month"
          style={{ width: '150px' }}
          aria-label="조회할 달"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
        {view.data && (
          <span style={mutedText}>
            찍은 날 {byDay.length}일
            {/* 공휴일에 일한 날은 대체휴무 탭이 세는 값이라 여기서는 「있다」 까지만 말한다. */}
            {view.data.holidays.some((holiday) => holiday.worked)
              && ` · 공휴일 근무 ${view.data.holidays.filter((holiday) => holiday.worked).length}일`}
          </span>
        )}
      </div>

      {view.isPending ? (
        <div className="flex justify-center py-10"><div className="spinner w-8 h-8" /></div>
      ) : view.error ? (
        <Notice tone="error">출퇴근 기록을 불러오지 못했습니다.</Notice>
      ) : byDay.length === 0 ? (
        <p className="py-8 text-center" style={mutedText}>이 달에 찍은 기록이 없습니다.</p>
      ) : (
        byDay.map((day) => (
          <div key={day.date} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
            <div className="flex items-center gap-3 flex-wrap">
              <span style={{ fontWeight: 600, width: '92px' }}>{day.date}</span>
              <span style={{ width: '120px' }}>출근 {day.in ? timeOf(day.in.occurredAt) : '—'}</span>
              <span style={{ width: '120px' }}>퇴근 {day.out ? timeOf(day.out.occurredAt) : '—'}</span>
              {/* 사람 손을 탄 줄은 그 사실을 말한다 — 위치도 IP 도 없는 줄이라 구분이 필요하다. */}
              {(day.in?.corrected || day.out?.corrected) && <span className="badge badge-gray">정정</span>}
              {!day.out && <span className="badge badge-yellow">퇴근 없음</span>}
              <button
                className="ml-auto"
                style={{ ...mutedText, textDecoration: 'underline' }}
                onClick={() => setCorrecting(correcting === day.date ? null : day.date)}
              >
                정정 요청
              </button>
            </div>
            {correcting === day.date && (
              <CorrectionForm date={day.date} onDone={() => setCorrecting(null)} />
            )}
          </div>
        ))
      )}

      <CorrectionHistory />
    </div>
  );
}

/** 정정 요청 폼 — 바라는 시각과 사유. 둘 중 하나는 적어야 서버가 받는다. */
function CorrectionForm({ date, onDone }: { date: string; onDone: () => void }) {
  const request = useRequestCorrection();
  const [wantedIn, setWantedIn] = useState('');
  const [wantedOut, setWantedOut] = useState('');
  const [reason, setReason] = useState('');

  const send = () => {
    request.mutate(
      {
        targetDate: date,
        wantedIn: wantedIn || null,
        wantedOut: wantedOut || null,
        reason: reason.trim(),
      },
      { onSuccess: onDone },
    );
  };

  return (
    <div className="rounded-lg space-y-2" style={{ border: '1px solid var(--border)', padding: '10px 12px', marginTop: '6px' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span style={mutedText}>출근</span>
        <input className="input-field" type="time" style={{ width: '120px' }} aria-label="바라는 출근 시각" value={wantedIn} onChange={(e) => setWantedIn(e.target.value)} />
        <span style={mutedText}>퇴근</span>
        <input className="input-field" type="time" style={{ width: '120px' }} aria-label="바라는 퇴근 시각" value={wantedOut} onChange={(e) => setWantedOut(e.target.value)} />
      </div>
      <input
        className="input-field"
        placeholder="사유 — 무엇이 어떻게 잘못 찍혔는지 적어 주세요."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex gap-2 justify-end">
        <button className="btn-secondary" style={compactButton} onClick={onDone}>취소</button>
        <button
          className="btn-primary"
          style={compactButton}
          disabled={request.isPending || !reason.trim() || (!wantedIn && !wantedOut)}
          onClick={send}
        >
          {request.isPending ? '보내는 중...' : '요청'}
        </button>
      </div>
      {request.isError && <p style={{ fontSize: '12px', color: 'var(--danger)' }}>{request.error.message}</p>}
    </div>
  );
}

/** 자발적 근무 신청 폼 — 근무일(오늘부터)·시작·끝·업무 내용. 자정을 넘기면 서버가 두 장으로 내라고 답한다. */
function VoluntaryForm({ onDone }: { onDone: () => void }) {
  const request = useRequestVoluntaryWork();
  const [workDate, setWorkDate] = useState(() => localToday());
  const [start, setStart] = useState('18:00');
  const [end, setEnd] = useState('');
  const [content, setContent] = useState('');

  const send = () => {
    request.mutate({ workDate, start, end, content: content.trim() }, { onSuccess: onDone });
  };

  return (
    <div className="rounded-lg space-y-2" style={{ border: '1px solid var(--border)', padding: '10px 12px' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <input className="input-field" type="date" style={{ width: '150px' }} aria-label="근무일" min={localToday()} value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
        <input className="input-field" type="time" style={{ width: '120px' }} aria-label="시작 시각" value={start} onChange={(e) => setStart(e.target.value)} />
        <span style={mutedText}>~</span>
        <input className="input-field" type="time" style={{ width: '120px' }} aria-label="끝 시각" value={end} onChange={(e) => setEnd(e.target.value)} />
      </div>
      <input
        className="input-field"
        placeholder="업무 내용"
        maxLength={300}
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />
      <div className="flex gap-2 justify-end">
        <button className="btn-secondary" style={compactButton} onClick={onDone}>닫기</button>
        <button
          className="btn-primary"
          style={compactButton}
          disabled={request.isPending || !workDate || !start || !end || !content.trim()}
          onClick={send}
        >
          {request.isPending ? '보내는 중...' : '신청'}
        </button>
      </div>
      {request.isError && <p style={{ fontSize: '12px', color: 'var(--danger)' }}>{request.error.message}</p>}
    </div>
  );
}

/** 낸 자발적 근무 신청과 그 결과. 확인 중인 것만 내릴 수 있다. */
function VoluntaryHistory() {
  const { data } = useMyVoluntaryWorks();
  const withdraw = useWithdrawVoluntaryWork();
  const rows = data ?? [];

  if (rows.length === 0) return null;

  return (
    <div>
      <p style={sectionTitle}>자발적 근무 신청</p>
      {rows.map((row) => (
        <div key={row.id} className="flex items-center gap-2 flex-wrap" style={{ padding: '6px 0', fontSize: '13px' }}>
          <span className={VOLUNTARY_BADGE[row.status].className}>{VOLUNTARY_BADGE[row.status].label}</span>
          <span style={{ fontWeight: 600 }}>{row.workDate}</span>
          <span className="num">{timeOf(row.startAt)} ~ {timeOf(row.endAt)}</span>
          <span style={mutedText}>{row.content}</span>
          {row.reviewNote && <span style={mutedText}>— {row.reviewNote}</span>}
          {row.status === 'REQUESTED' && (
            <button
              className="ml-auto"
              style={{ ...mutedText, textDecoration: 'underline' }}
              disabled={withdraw.isPending}
              onClick={() => withdraw.mutate(row.id)}
            >
              취소
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

const VOLUNTARY_BADGE: Record<'REQUESTED' | 'APPROVED' | 'REJECTED', { className: string; label: string }> = {
  REQUESTED: { className: 'badge badge-yellow', label: '확인 중' },
  APPROVED: { className: 'badge badge-green', label: '승인' },
  REJECTED: { className: 'badge badge-gray', label: '반려' },
};

/** 오늘(이 PC 의 날짜) — `toISOString` 은 UTC 라 아침 9시 전에는 어제가 된다. */
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 낸 요청과 그 결과. 반려 사유가 여기 남아야 무엇을 고쳐 다시 낼지 알 수 있다. */
function CorrectionHistory() {
  const { data } = useMyCorrections();
  const withdraw = useWithdrawCorrection();
  const rows = data ?? [];

  if (rows.length === 0) return null;

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
      <p style={sectionTitle}>정정 요청</p>
      {rows.map((row) => (
        <div key={row.id} className="flex items-center gap-2 flex-wrap" style={{ padding: '6px 0', fontSize: '13px' }}>
          <span className={STATUS_BADGE[row.status].className}>{STATUS_BADGE[row.status].label}</span>
          <span style={{ fontWeight: 600 }}>{row.targetDate}</span>
          <span style={mutedText}>
            {row.wantedIn ? `출근 ${row.wantedIn.slice(0, 5)}` : ''}
            {row.wantedIn && row.wantedOut ? ' · ' : ''}
            {row.wantedOut ? `퇴근 ${row.wantedOut.slice(0, 5)}` : ''}
          </span>
          <span style={mutedText}>{row.reason}</span>
          {row.reviewNote && <span style={mutedText}>— {row.reviewNote}</span>}
          {row.status === 'PENDING' && (
            <button
              className="ml-auto"
              style={{ ...mutedText, textDecoration: 'underline' }}
              disabled={withdraw.isPending}
              onClick={() => withdraw.mutate(row.id)}
            >
              취소
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

const STATUS_BADGE: Record<'PENDING' | 'APPROVED' | 'REJECTED', { className: string; label: string }> = {
  PENDING: { className: 'badge badge-yellow', label: '확인 중' },
  APPROVED: { className: 'badge badge-green', label: '반영' },
  REJECTED: { className: 'badge badge-gray', label: '반려' },
};

const KIND_LABEL: Record<'IN' | 'OUT', string> = { IN: '출근', OUT: '퇴근' };

/**
 * 하루 = 한 줄. 출근·퇴근이 각각 한 줄로 오므로({@code hr_attendances}) 화면에서 묶는다 —
 * 서버가 묶지 않는 이유는 근무시간 판정에 사내 규정이 먼저 서야 하기 때문이다(V228 §4).
 */
function groupByDay(rows: AttendanceRow[]): Array<{ date: string; in: AttendanceRow | null; out: AttendanceRow | null }> {
  const byDate = new Map<string, { date: string; in: AttendanceRow | null; out: AttendanceRow | null }>();
  for (const row of rows) {
    const date = row.occurredAt.slice(0, 10);
    const day = byDate.get(date) ?? { date, in: null, out: null };
    // 같은 종류가 둘이면 «처음 출근 · 마지막 퇴근» 이 그날의 사실에 가깝다.
    if (row.kind === 'IN') day.in = day.in ?? row;
    else day.out = row;
    byDate.set(date, day);
  }
  return [...byDate.values()].sort((a, b) => b.date.localeCompare(a.date));
}

function timeOf(value: string): string {
  return new Date(value).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}

const compactButton = { fontSize: '12px', padding: '5px 12px' } as const;
const mutedText = { fontSize: '12px', color: 'var(--text-muted)' } as const;
const sectionTitle = { fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' } as const;
